import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { CertificationService } from '../../core/services/certification.service';
import { OrganizationService } from '../../core/services/organization.service';
import { User } from '../../core/models/user.model';
import { Organization } from '../../core/models/organization.model';
import {
  Certification,
  CertificationTypeInfo,
  CertificationStatus,
  getCertificationStatus,
  ORGANIZATION_CERTIFICATION_TYPES,
  USER_CERTIFICATION_TYPES
} from '../../core/models/certification.model';
import { toCdnUrl } from '../../shared/utils/format.utils';
import { toDisplayDate } from '../../shared/utils/date.utils';
import { MembershipRoles } from 'src/app/core/models';
import { OrganizationContextService } from 'src/app/core/services/organization-context.service';

/**
 * Certifications Component
 * Displays organization and user certifications in a tabbed interface
 */
@Component({
  selector: 'app-certifications',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './certifications.component.html',
  styleUrls: ['./certifications.component.scss']
})
export class CertificationsComponent implements OnInit {
  activeTab: 'organization' | 'user' = 'organization';
  currentUser: User | null = null;
  isLoading = false;
  canEditOrganization = false;

  // Certification type metadata
  organizationCertTypes = ORGANIZATION_CERTIFICATION_TYPES;
  userCertTypes = USER_CERTIFICATION_TYPES;

  // Certification data from API
  organizationCertifications: Certification[] = [];
  userCertifications: Certification[] = [];

  // Organization info
  currentOrganization: Organization | null = null;
  organizationName = '';

  constructor(
    private router: Router,
    private authService: AuthService,
    private certificationService: CertificationService,
    private organizationService: OrganizationService,
    private organizationContextService: OrganizationContextService
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      this.updateCanEdit();
    });
    this.loadCertifications();
  }

  /**
   * Load certifications from backend
   */
  async loadCertifications(): Promise<void> {
    this.isLoading = true;

    try {
      // Load current organization
      this.currentOrganization = await this.organizationService.getMyOrganization();
      this.organizationName = this.currentOrganization?.name || 'My Organization';

      // Load organization certifications if user has an organization
      if (this.currentOrganization) {
        this.organizationCertifications = await this.certificationService.getOrganizationCertifications(
          this.currentOrganization.id
        );
      } else {
        this.organizationCertifications = [];
      }
      
      // Load user's personal certifications
      this.userCertifications = await this.certificationService.getProfileCertifications();
      this.updateCanEdit();
    } catch (error) {
      console.error('Error loading certifications:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private updateCanEdit(): void {
    const claims = this.currentUser?.customClaims;
    const orgId = this.organizationContextService.getCurrentOrganizationId();;
    this.canEditOrganization = !!claims?.isAdmin() || (!!orgId && (
      !!claims?.hasRoleInOrganization(orgId, MembershipRoles.Owner) ||
      !!claims?.hasRoleInOrganization(orgId, MembershipRoles.ChiefSupervisor)
    ));
    if (!this.canEditOrganization) {
      this.activeTab = 'user'
    }
  }

  /**
   * Switch between tabs
   */
  setActiveTab(tab: 'organization' | 'user'): void {
    this.activeTab = tab;
  }

  /**
   * Get certification type label
   */
  getCertTypeLabel(certType: string): string {
    const allTypes = [...ORGANIZATION_CERTIFICATION_TYPES, ...USER_CERTIFICATION_TYPES];
    const found = allTypes.find(t => t.value === certType);
    return found?.label || certType;
  }

  /**
   * Get certification type description
   */
  getCertTypeDescription(certType: string): string {
    const allTypes = [...ORGANIZATION_CERTIFICATION_TYPES, ...USER_CERTIFICATION_TYPES];
    const found = allTypes.find(t => t.value === certType);
    return found?.description || '';
  }

  /**
   * Check if a required certification type is uploaded
   */
  isCertTypeUploaded(certType: CertificationTypeInfo): boolean {
    if (certType.ownerType === 'organization') {
      return this.organizationCertifications.some(c => c.certificationType === certType.value);
    }
    return this.userCertifications.some(c => c.certificationType === certType.value);
  }

  /**
   * Get certification for a specific type
   */
  getCertificationForType(certType: CertificationTypeInfo): Certification | undefined {
    if (certType.ownerType === 'organization') {
      return this.organizationCertifications.find(c => c.certificationType === certType.value);
    }
    return this.userCertifications.find(c => c.certificationType === certType.value);
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(cert: Certification): string {
    let statusClass = getCertificationStatus(cert.status, cert.expirationDate);

    switch (statusClass) {
      case 'active':
        return 'badge-active';
      case 'expired':
        return 'badge-expired';
      case 'expiring-soon':
        return 'badge-expiring';
      case 'archived':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get status display text
   */
  getStatusText(cert: Certification): string {
    let statusClass = getCertificationStatus(cert.status, cert.expirationDate);

    switch (statusClass) {
      case 'active':
        return 'Active';
      case 'expired':
        return 'Expired';
      case 'expiring-soon':
        return 'Expiring Soon';
      case 'archived':
        return 'Archived';
      default:
        return statusClass;
    }
  }

  /**
   * Format date for display.
   * Issue/expiration dates are Postgres `date` columns serialized as UTC
   * midnight, so they're pinned to local midnight first — otherwise
   * "2029-01-01" renders as "Dec 31, 2028" in every US timezone.
   */
  formatDate(dateString: string): string {
    const date = toDisplayDate(dateString);
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Add new certification
   */
  addCertification(certType: CertificationTypeInfo): void {
    this.router.navigate(['/certifications/add'], {
      queryParams: {
        type: certType.value,
        ownerType: certType.ownerType
      }
    });
  }

  addCertificationText(certType: CertificationTypeInfo): string {
    switch (certType.value) {
      case 'state-applicator':
        return 'Add for Another State';
      case 'other':
        return 'Add for Another Unlisted Certification';
      default:
        return `Add for Another ${certType.label}`;
    }
  }

  /**
   * Edit certification
   */
  editCertification(cert: Certification): void {
    this.router.navigate(['/certifications', cert.id, 'edit']);
  }

  /**
   * Renew certification
   */
  renewCertification(cert: Certification): void {
    this.router.navigate(['/certifications/add'], {
      queryParams: {
        type: cert.certificationType,
        ownerType: cert.ownerType,
        renew: 'true',
        previousId: cert.id
      }
    });
  }

  /**
   * Check if a cert type allows multiple uploads (e.g. state-applicator for different states)
   */
  allowsMultiple(certType: CertificationTypeInfo): boolean {
    return certType.value === 'state-applicator' || certType.value === 'other';
  }

  /**
   * Get ALL certifications for a specific type (for multi-cert types like state-applicator)
   */
  getCertificationsForType(certType: CertificationTypeInfo): Certification[] {
    if (certType.ownerType === 'organization') {
      return this.organizationCertifications.filter(c => c.certificationType === certType.value);
    }
    return this.userCertifications.filter(c => c.certificationType === certType.value);
  }

  /**
   * View certification document via CDN with signed URL query params
   */
  async viewDocument(cert: Certification): Promise<void> {
    if (cert.documentUrl) {
      try {
        const signedUrl = await this.certificationService.getDownloadUrl(cert.id);
        window.open(toCdnUrl(signedUrl), '_blank');
      } catch (error) {
        console.error('Failed to get download URL:', error);
      }
    }
  }

  /**
   * Navigate back to dashboard
   */
  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  /**
   * Get uploaded organization certifications
   */
  get uploadedOrgCertTypes(): CertificationTypeInfo[] {
    return this.organizationCertTypes.filter(t => this.isCertTypeUploaded(t));
  }

  /**
   * Get missing required organization certifications
   */
  get missingOrgCertTypes(): CertificationTypeInfo[] {
    return this.organizationCertTypes.filter(t => t.isRequired && !this.isCertTypeUploaded(t));
  }

  /**
   * Get uploaded user certifications (both required and optional)
   */
  get uploadedUserCertTypes(): CertificationTypeInfo[] {
    return this.userCertTypes.filter(t => this.isCertTypeUploaded(t));
  }

  /**
   * Get missing required user certifications
   */
  get missingUserCertTypes(): CertificationTypeInfo[] {
    return this.userCertTypes.filter(t => t.isRequired && !this.isCertTypeUploaded(t));
  }

  /**
   * Get optional user certification types (not uploaded)
   */
  get optionalUserCertTypes(): CertificationTypeInfo[] {
    return this.userCertTypes.filter(t => !t.isRequired && !this.isCertTypeUploaded(t));
  }

  /**
   * Get optional organization certification types (not uploaded)
   */
  get optionalOrgCertTypes(): CertificationTypeInfo[] {
    return this.organizationCertTypes.filter(t => !t.isRequired && !this.isCertTypeUploaded(t));
  }

  /**
   * Get count of missing required certifications for organization
   */
  get missingOrgCertsCount(): number {
    return this.organizationCertTypes
      .filter(t => t.isRequired && !this.isCertTypeUploaded(t))
      .length;
  }

  /**
   * Get count of missing required certifications for user
   */
  get missingUserCertsCount(): number {
    return this.userCertTypes
      .filter(t => t.isRequired && !this.isCertTypeUploaded(t))
      .length;
  }
}
